/**
 * Hat Scratch Popup — Gamified email capture
 * Three hats with custom images loaded from CDN
 * Scratch to reveal discount → enter email → get code
 */
if (!customElements.get('hat-scratch-popup')) {
  customElements.define('hat-scratch-popup', class HatScratchPopup extends HTMLElement {
    constructor() {
      super();
      this.currentStep = 'game';
      this.revealedIndex = -1;
      this.activeIndex = -1;
      this.isScratching = false;
      this.canvases = [];
      this.contexts = [];
      this.initialOpaque = [];
      this.lastCheckTime = 0;
      this.CHECK_INTERVAL = 200;
      this.REVEAL_THRESHOLD = 65;
      this.BRUSH_RADIUS = 15;
    }

    connectedCallback() {
      this.balls = this.querySelectorAll('.hsp-ball');
      this.steps = {
        game: this.querySelector('[data-step="game"]'),
        email: this.querySelector('[data-step="email"]'),
        discount: this.querySelector('[data-step="discount"]')
      };

      this.discountCode = this.dataset.discountCode || 'FIRST10';
      this.discountText = this.dataset.discountText || '10% OFF';
      this.enableConfetti = this.dataset.confetti === 'true';
      this.motionReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      var self = this;
      this._canvasRetries = 0;
      this._waitAndInitCanvases();

      this.observeNewsletterForm();
      this.checkSessionResume();

      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        this.setupDesignMode();
      }
    }

    /* ========== WAIT FOR VISIBILITY BEFORE INIT ========== */
    _waitAndInitCanvases() {
      var self = this;
      var firstBall = this.balls[0];
      // Popup starts hidden — offsetWidth is 0 until visible
      if (firstBall && firstBall.offsetWidth === 0 && this._canvasRetries < 100) {
        this._canvasRetries++;
        setTimeout(function() { self._waitAndInitCanvases(); }, 80);
        return;
      }
      this.initCanvases();
    }

    /* ========== IMAGE-BASED HAT DRAWING ========== */

    drawHatImage(ctx, canvas, w, h, imgUrl, ballEl, index) {
      // Transparent canvas — only hat image pixels are drawn (no background fill)
      var self = this;
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function() {
        // Use full contain scale — matches CSS mask-size: contain on content layer
        var scale = Math.min(w / img.width, h / img.height);
        var iw = img.width * scale;
        var ih = img.height * scale;
        var ix = (w - iw) / 2;
        var iy = (h - ih) / 2;
        ctx.drawImage(img, ix, iy, iw, ih);

        // Count initial opaque pixels for scratch percentage detection
        var imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        var pixels = imageData.data;
        var opaque = 0;
        for (var p = 3; p < pixels.length; p += 64) {
          if (pixels[p] > 0) opaque++;
        }
        self.initialOpaque[index] = opaque;
      };
      img.onerror = function() {
        ctx.fillStyle = 'rgba(255,255,255,0.3)';
        ctx.font = 'bold ' + (w * 0.4) + 'px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('?', w / 2, h / 2);
        self.initialOpaque[index] = 1;
      };
      img.src = imgUrl;
    }

    /* ========== CANVAS INIT ========== */
    initCanvases() {
      var self = this;

      this.balls.forEach(function(ball, i) {
        var canvas = ball.querySelector('.hsp-ball__canvas');
        if (!canvas) return;

        var w = ball.offsetWidth;
        var h = ball.offsetHeight;
        if (!w || !h) return; // Skip — visibility retry will call again

        var dpr = window.devicePixelRatio || 1;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        // Do NOT set canvas.style.width/height — let CSS (width:100%;height:100%) handle display sizing

        var ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.scale(dpr, dpr);

        var imgUrl = ball.dataset.image;
        if (imgUrl) {
          self.drawHatImage(ctx, canvas, w, h, imgUrl, ball, i);
        }

        self.canvases.push(canvas);
        self.contexts.push(ctx);

        if (self.motionReduced) {
          self.bindClickReveal(canvas, i);
        } else {
          self.bindScratchEvents(canvas, i, ball);
        }
      });
    }

    /* ========== SCRATCH EVENTS ========== */
    bindScratchEvents(canvas, index, ballEl) {
      var self = this;
      var lastPos = null;

      var getPos = function(e) {
        var rect = canvas.getBoundingClientRect();
        var touch = e.touches ? e.touches[0] : e;
        return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
      };

      // Move/end handlers added to document on start, removed on end
      var onMouseMove = function(e) {
        if (self.revealedIndex !== -1) return;
        var pos = getPos(e);
        self.scratchLine(index, lastPos, pos, ballEl);
        lastPos = pos;
      };
      var onMouseUp = function() {
        if (self.revealedIndex === -1) self.checkReveal(index);
        self.isScratching = false;
        lastPos = null;
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
      var onTouchMove = function(e) {
        if (self.revealedIndex !== -1) return;
        e.preventDefault();
        var pos = getPos(e);
        self.scratchLine(index, lastPos, pos, ballEl);
        lastPos = pos;
      };
      var onTouchEnd = function() {
        if (self.revealedIndex === -1) self.checkReveal(index);
        self.isScratching = false;
        lastPos = null;
        document.removeEventListener('touchmove', onTouchMove);
        document.removeEventListener('touchend', onTouchEnd);
      };

      // Start from anywhere on the ball container (not just the canvas)
      ballEl.addEventListener('mousedown', function(e) {
        if (self.revealedIndex !== -1) return;
        self.isScratching = true;
        self.activeIndex = index;
        lastPos = getPos(e);
        self.scratch(index, lastPos, ballEl);
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      });

      ballEl.addEventListener('touchstart', function(e) {
        if (self.revealedIndex !== -1) return;
        e.preventDefault();
        self.isScratching = true;
        self.activeIndex = index;
        lastPos = getPos(e);
        self.scratch(index, lastPos, ballEl);
        document.addEventListener('touchmove', onTouchMove, { passive: false });
        document.addEventListener('touchend', onTouchEnd);
      }, { passive: false });
    }

    scratch(index, pos, ballEl) {
      var ctx = this.contexts[index];
      if (!ctx) return;
      ctx.globalCompositeOperation = 'destination-out';
      ctx.globalAlpha = 1.0;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, this.BRUSH_RADIUS, 0, Math.PI * 2);
      ctx.fill();
      this.spawnSparkle(ballEl, pos);
      this.throttledCheck(index);
    }

    scratchLine(index, from, to, ballEl) {
      if (!from || !to) { this.scratch(index, to || from, ballEl); return; }
      var ctx = this.contexts[index];
      if (!ctx) return;
      ctx.globalCompositeOperation = 'destination-out';
      ctx.globalAlpha = 1.0;
      ctx.lineWidth = this.BRUSH_RADIUS * 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(to.x, to.y);
      ctx.stroke();

      // Sparkles along the stroke
      if (Math.random() < 0.4) this.spawnSparkle(ballEl, to);
      this.throttledCheck(index);
    }

    /* ========== SPARKLE EFFECT ========== */
    spawnSparkle(ballEl, pos) {
      var sparkle = document.createElement('div');
      sparkle.className = 'hsp-sparkle';
      sparkle.style.left = pos.x + 'px';
      sparkle.style.top = pos.y + 'px';
      sparkle.style.setProperty('--dx', (Math.random() * 30 - 15) + 'px');
      sparkle.style.setProperty('--dy', (Math.random() * -25 - 5) + 'px');
      var colors = ['#a8c4e0', '#ffffff', '#5b8cb8', '#d0e2f2'];
      sparkle.style.background = colors[Math.floor(Math.random() * colors.length)];
      ballEl.appendChild(sparkle);
      setTimeout(function() { sparkle.remove(); }, 500);
    }

    throttledCheck(index) {
      var now = Date.now();
      if (now - this.lastCheckTime > this.CHECK_INTERVAL) {
        this.lastCheckTime = now;
        this.checkReveal(index);
      }
    }

    checkReveal(index) {
      var canvas = this.canvases[index];
      var ctx = this.contexts[index];
      if (!canvas || !ctx) return;
      var initialOpaque = this.initialOpaque[index];
      if (!initialOpaque || initialOpaque <= 0) return;

      var imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      var pixels = imageData.data;
      var currentOpaque = 0;
      for (var i = 3; i < pixels.length; i += 64) {
        if (pixels[i] > 0) currentOpaque++;
      }
      var scratchedPct = ((initialOpaque - currentOpaque) / initialOpaque) * 100;
      if (scratchedPct >= this.REVEAL_THRESHOLD) this.completeReveal(index);
    }

    /* ========== CLICK REVEAL (reduced motion) ========== */
    bindClickReveal(canvas, index) {
      var self = this;
      canvas.style.cursor = 'pointer';
      canvas.setAttribute('role', 'button');
      canvas.setAttribute('tabindex', '0');
      canvas.addEventListener('click', function() {
        if (self.revealedIndex === -1) self.completeReveal(index);
      });
      canvas.addEventListener('keydown', function(e) {
        if ((e.key === 'Enter' || e.key === ' ') && self.revealedIndex === -1) {
          e.preventDefault();
          self.completeReveal(index);
        }
      });
    }

    /* ========== COMPLETE REVEAL ========== */
    completeReveal(index) {
      if (this.revealedIndex !== -1) return;
      this.revealedIndex = index;
      this.isScratching = false;

      var ball = this.balls[index];
      var canvas = this.canvases[index];

      // Fade remaining canvas
      canvas.style.transition = 'opacity 0.5s ease-out';
      canvas.style.opacity = '0';
      canvas.style.pointerEvents = 'none';

      // Dim others
      this.balls.forEach(function(b, i) {
        if (i !== index) b.classList.add('hsp-ball--dimmed');
      });

      ball.classList.add('hsp-ball--revealed');

      var discountEl = ball.querySelector('.hsp-ball__discount-text');
      if (discountEl) discountEl.classList.add('hsp-ball__discount-text--visible');

      // Make content clickable
      var content = ball.querySelector('.hsp-ball__content');
      if (content) {
        content.setAttribute('role', 'button');
        content.setAttribute('tabindex', '0');
        content.style.cursor = 'pointer';
        var self = this;
        content.addEventListener('click', function() { self.goToStep('email'); });
        content.addEventListener('keydown', function(e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); self.goToStep('email'); }
        });
        setTimeout(function() { content.focus(); }, 500);
      }

      // Add "Tap to claim!" prompt below the revealed hat
      var claimHint = document.createElement('div');
      claimHint.className = 'hsp-claim-hint';
      claimHint.textContent = 'Tap to claim!';
      claimHint.addEventListener('click', function() { self.goToStep('email'); });
      ball.appendChild(claimHint);

      var hint = this.querySelector('.hsp-hint');
      if (hint) hint.style.display = 'none';

      if (this.enableConfetti) this.fireConfetti();
      this.announce(this.discountText + ' revealed! Tap to claim your discount.');
    }

    /* ========== STEP TRANSITIONS ========== */
    goToStep(step) {
      var oldEl = this.steps[this.currentStep];
      var newEl = this.steps[step];
      if (!oldEl || !newEl) return;
      var self = this;

      if (this.motionReduced) {
        oldEl.hidden = true; newEl.hidden = false; self.focusStep(newEl);
      } else {
        oldEl.classList.add('hsp-step--exiting');
        setTimeout(function() {
          oldEl.hidden = true; oldEl.classList.remove('hsp-step--exiting');
          newEl.hidden = false; newEl.classList.add('hsp-step--entering');
          setTimeout(function() { newEl.classList.remove('hsp-step--entering'); }, 450);
          self.focusStep(newEl);
        }, 350);
      }
      this.currentStep = step;
      this.setAttribute('data-step', step);
    }

    focusStep(el) {
      setTimeout(function() {
        var t = el.querySelector('input[type="email"], button, [tabindex="0"]');
        if (t) t.focus();
      }, 100);
    }

    /* ========== NEWSLETTER FORM ========== */
    observeNewsletterForm() {
      var form = this.querySelector('form');
      if (!form) return;
      var self = this;

      // Use capture phase + stopImmediatePropagation to beat the custom element's handler
      form.addEventListener('submit', function(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        var emailInput = form.querySelector('input[type="email"]');
        if (!emailInput || !emailInput.value) return;

        var submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) { submitBtn.disabled = true; submitBtn.classList.add('btn--loading'); }

        try {
          sessionStorage.setItem('hsp_pending_discount', JSON.stringify({ code: self.discountCode, step: 'discount' }));
        } catch (err) {}

        var formData = new FormData(form);

        fetch(form.action || '/contact', {
          method: 'POST',
          body: new URLSearchParams(formData),
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        }).then(function() {
          self.goToStep('discount');
          try { sessionStorage.removeItem('hsp_pending_discount'); } catch (err) {}
        }).catch(function() {
          // Shopify may redirect causing a fetch error — treat as success
          self.goToStep('discount');
          try { sessionStorage.removeItem('hsp_pending_discount'); } catch (err) {}
        }).finally(function() {
          if (submitBtn) { submitBtn.disabled = false; submitBtn.classList.remove('btn--loading'); }
        });
      }, true); // capture phase
    }

    checkSessionResume() {
      try {
        var saved = sessionStorage.getItem('hsp_pending_discount');
        if (saved) {
          var data = JSON.parse(saved);
          if (data && data.step === 'discount') {
            this.goToStep('discount');
            sessionStorage.removeItem('hsp_pending_discount');
          }
        }
      } catch (err) {}
    }

    /* ========== CONFETTI ========== */
    fireConfetti() {
      var container = this.querySelector('.hsp-confetti-container');
      if (!container) return;
      var colors = ['#ff6b6b', '#feca57', '#48dbfb', '#ff9ff3', '#54a0ff', '#5f27cd', '#00d2d3'];
      for (var i = 0; i < 60; i++) {
        var piece = document.createElement('div');
        piece.className = 'hsp-confetti-piece';
        piece.style.setProperty('--x', (Math.random() * 100) + '%');
        piece.style.setProperty('--delay', (Math.random() * 0.6) + 's');
        piece.style.setProperty('--rotation', (Math.random() * 360) + 'deg');
        piece.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        container.appendChild(piece);
      }
      setTimeout(function() { container.innerHTML = ''; }, 3500);
    }

    announce(message) {
      var a = this.querySelector('.hsp-sr-announcer');
      if (!a) {
        a = document.createElement('div');
        a.className = 'hsp-sr-announcer hsp-sr-only';
        a.setAttribute('role', 'status');
        a.setAttribute('aria-live', 'polite');
        this.appendChild(a);
      }
      a.textContent = '';
      setTimeout(function() { a.textContent = message; }, 100);
    }

    setupDesignMode() {
      var self = this;
      var section = this.closest('.shopify-section');
      if (!section) return;
      section.addEventListener('shopify:block:select', function(e) {
        var t = e.target.getAttribute('data-block-type');
        if (t === 'hat_game') self.previewStep('game');
        else if (t === 'email_step') self.previewStep('email');
        else if (t === 'discount_step') self.previewStep('discount');
      });
    }

    previewStep(step) {
      var self = this;
      Object.keys(this.steps).forEach(function(k) { if (self.steps[k]) self.steps[k].hidden = (k !== step); });
      this.setAttribute('data-step', step);
    }
  });
}
